import { z } from "zod";

const NPI_REGEX = /^\d{10}$/;

const providerNpiSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  npi: z.string(),
  specialties: z.array(
    z.object({
      code: z.string(),
      desc: z.string().nullish(),
      primary: z.boolean().nullish(),
      state: z.string().nullish(),
      taxonomy_group: z.string().nullish(),
    }),
  ),
  providerTypeId: z.string().nullable(),
  middleName: z.string().nullish(),
  suffixName: z.string().nullish(),
});

/**
 * One schema for adding a provider and for editing one already on the roster, so
 * the two dialogs cannot drift on what counts as a valid provider. The edit
 * dialog leaves `sendInvitation` false and `providerNpi` unused — sending is its
 * own action there, and the person has already been identified.
 */
export const providerFormSchema = z
  .object({
    email: z
      .string()
      .refine(
        (value) => !value || z.string().email().safeParse(value).success,
        {
          message: "Enter a valid email address",
        },
      ),
    sendInvitation: z.boolean(),
    providerNpi: providerNpiSchema.nullish(),
    firstName: z.string().max(200).optional(),
    lastName: z.string().max(200).optional(),
    phone: z.string().max(50).optional(),
    npi: z
      .string()
      .optional()
      .refine((value) => !value || NPI_REGEX.test(value), {
        message: "NPI must be exactly 10 digits",
      }),
    healthcareProfessionalType: z
      .object({ id: z.string(), name: z.string() })
      .nullish(),
    specialtyCodes: z.array(z.string()).optional(),
  })
  .refine((value) => !value.sendInvitation || Boolean(value.email), {
    path: ["email"],
    message: "Email is required to send an invitation",
  })
  .refine(
    (value) =>
      Boolean(value.email || value.npi || value.firstName || value.lastName),
    {
      // Reported at the form level, not on `firstName`: that field is only
      // mounted once NPI lookup or manual entry is open, so an error attached to
      // it would have nowhere to render and the submit would fail in silence.
      path: ["root"],
      message: "Enter a provider name, NPI, or email",
    },
  );

export type ProviderFormValues = z.infer<typeof providerFormSchema>;

/**
 * `PhoneInput` seeds an untouched field with its default country's dial code, so
 * a form nobody typed a number into still reports "+1". Submitting that would
 * store a phone number that is only a country code — enough to look like contact
 * information on the roster and to prefill the provider's onboarding with
 * nothing.
 */
export const toSubmittedPhone = (phone: string | undefined): string | null => {
  const digits = (phone ?? "").replace(/\D/g, "");
  const isDialCodeOnly = digits.length <= 1;
  return isDialCodeOnly ? null : phone!.trim();
};

export const EMPTY_PROVIDER_FORM_VALUES: ProviderFormValues = {
  email: "",
  sendInvitation: false,
  providerNpi: null,
  firstName: "",
  lastName: "",
  phone: "",
  npi: "",
  healthcareProfessionalType: undefined,
  specialtyCodes: [],
};
